import React from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import currency from "currency.js";
import { Constants, helpers, Enums } from "@shared";
import PropTypes from 'prop-types';
import { DateTime } from "luxon";
import { useSessionContext } from '@contexts';
import { Alerts } from "@common";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { TransactionBadge } from "@common";

const Bill = ({ currentBill, alerts }) => {

    const navigate = useNavigate(), location = useLocation();
    const { session, accounts, selectedAccount } = useSessionContext();

    const {
        idPayor,
        isGuarantor,
        creditChecked,
        paymentPreferences: { hasDefaultPaymentMethod, hasDefaultPaymentMethodDisabled, hasSecondaryPaymentMethod },
        accountPreferences: { isAutoPay },
        potDeadline
    } = session;

    const {
        balance,
        dueDate,
        invitations,
        idProductType,
        canPOTEnroll,
        totalPaid
    } = currentBill;

    const month = helpers.monthName(dueDate);
    const payorInvitation = invitations.find(i => i.idPayor === idPayor);
    const { isLate, isDue, isPaymentScheduled } = payorInvitation;
    const payorBalance = currency(payorInvitation.balance);
    const hasOutstanding = payorBalance.intValue !== 0;

    const sortedInvitations = invitations.sort(a => a.idPayor === idPayor ? -1 : 1);
    const invoiceBalance = currency(balance);

    const billAlerts = [...alerts];

    const resolveDefaults = helpers.shouldResolveDefaults(accounts, selectedAccount);

    const onPayClick = (makePayment) => {

        const paymentFlowOpts = [
            Constants.Flows.makePaymentAutoPay.id,
            {
                hasDefaultPaymentMethod,
                isAutoPay,
                makePayment,
                resolveDefaults
            },
            {
                currentBill,
                makePayment,
                account: { ...session }
            }
        ];

        if ((!hasDefaultPaymentMethod || hasDefaultPaymentMethodDisabled) && hasSecondaryPaymentMethod) {
            // if they have a secondary method we go to the settings page (methods)
            // so they can select that one as the default
            // and the flow simply ends at that point, they need to go back to dashboard
            // to enter payment flow again
            helpers.navigateTo(`/payment-method-settings`, location, navigate, undefined, {
                // TODO ideally state would be typed
                onClickPaymentFlow: paymentFlowOpts
            });
        } else {
            helpers.navigateStart(location, navigate, ...paymentFlowOpts);
        }

    };

    const modify = () => {

        const { invitations, ...rest } = currentBill
        const { idProductType } = rest;

        const invoicePayors = invitations.map((i) => {
            const { idPayor, idInvitation, paid, amount: payAmount, payPercentage, autodrawDate, fullName, email, isGuarantor } = i;
            const dueDay = DateTime.fromISO(autodrawDate).day;
            return (
                {
                    idPayor,
                    idInvitation,
                    fullName,
                    email,
                    payAmount,
                    payPercentage,
                    dueDay,
                    amountType: Enums.AmountType.Amount,
                    alreadySelected: true,
                    paid,
                    isGuarantor
                }
            );
        });

        helpers.navigateStart(location, navigate, Constants.Flows.updateEnrolledInvoice.id, undefined, {
            account: { ...session },
            idProductType,
            invoicePayors,
            invoice: { ...rest }
        });
    };

    const switchProduct = () => {

        const {
            paymentPreferences: { hasDefaultPaymentMethod },
            accountPreferences: { isAutoPay },
            creditChecked,
        } = session;

        const { pot: { enrolledPayors: invoicePayors, ...rest } } = currentBill;

        const updatedInvoice = { ...currentBill, total: undefined, ...rest };
        helpers.navigateStart(location, navigate, Constants.Flows.advance.id, {
            hasDefaultPaymentMethod,
            isAutoPay,
            creditChecked,
            skipPayors: invoicePayors.length > 1,
            update: true,
            idProductType: Enums.ProductType.SunboundPay,
        }, {
            account: { ...session },
            creditChecked,
            idProductType: Enums.ProductType.SunboundPay,
            invoicePayors,
            invoice: updatedInvoice
        });
    }

    const paymentEnabled = (idProductType === Enums.ProductType.Gateway || (idProductType === Enums.ProductType.SunboundPay && creditChecked));
    const hasPayments = currency(totalPaid).intValue !== 0;
    const showPassDue = isDue && !isLate && !isPaymentScheduled

    return (
        <div className="page-content-wrapper w-full gap-y-5">
            <h1 className="text-center font-bold text-2xl">My Dashboard</h1>
            <span className="text-gray-500">You can always call Sunbound at <b>{Constants.Help.Phone}</b> or <a className="underline" href={Constants.Help.FAQ} target="_blank" rel="noreferrer">visit our FAQ</a> if you have any other questions.</span>
            <Alerts alerts={billAlerts} />
            <div className="flex flex-col bg-sb-frame rounded-lg shadow-sb p-4 w-full items-center gap-y-4">
                <h1 className="text-center text-blue-text font-bold text-2xl">{`${month} Balance`}</h1>
                <h1 className="text-center font-semibold text-4xl">{invoiceBalance.format()}</h1>
                {
                    !isAutoPay && hasOutstanding && paymentEnabled &&
                    <div>
                        <button className="background-transparent font-semibold text-blue-on  px-3 py-1 text-lg outline-none focus:outline-none mr-1 mb-1" type="button" onClick={() => onPayClick(false)}>
                            Turn On Auto-Pay
                        </button>
                    </div>
                }
                {
                    isAutoPay &&
                    <div className="text-gray-500 text-2xl inline-flex">
                        <span className="inline-flex">Auto-pay is on<a href={Constants.Help.AutoPay} target="_blank" rel="noreferrer"><ExclamationCircleIcon className="h-6 ml-1" /></a></span>
                    </div>
                }
                {
                    hasOutstanding &&
                    <div className="flex flex-col space-y-2 w-full">
                        <button className="btn btn-primary font-bold rounded-full" disabled={!paymentEnabled} onClick={() => onPayClick(true)}>Make Payment</button>
                    </div>
                }
                {
                    showPassDue &&
                    <div id="pastdue-alert">
                        <span className="text-red-500 font-bold">Your payment is past due. Make a payment to avoid late fees.</span>
                    </div>
                }
                {
                    isLate &&
                    <div id="late-alert">
                        <span className="text-red-500 font-bold">Your payment is late. Make a payment as soon as possible.</span>
                    </div>
                }
                {
                    hasOutstanding && !paymentEnabled &&
                    <span>You’ll be able to make payments after resolving issues. See the Alerts Center for more information.</span>
                }
                {
                    idProductType !== Enums.ProductType.SunboundPay && isGuarantor && hasDefaultPaymentMethod &&
                    <div className="flex flex-col space-y-2 w-full">
                        <button className="btn btn-success font-bold rounded-full" disabled={!canPOTEnroll || hasPayments} onClick={switchProduct}>Pay Over Time</button>
                        {
                            canPOTEnroll && !hasPayments &&
                            <span className="text-gray-500 ">Extend your due date to <b>{`${month} ${helpers.dayOrdinal(potDeadline)} `}</b> and pay in your preferred number of installments. All for only a 1% fee!</span>
                        }
                        {
                            canPOTEnroll && hasPayments &&
                            <div id="partial-payment-alert">
                                <span className="text-gray-500 ">Call <b>{Constants.Help.Phone}</b> to ask about Pay Over Time for your partially paid balance.</span>
                            </div>
                        }
                        {
                            !canPOTEnroll &&
                            <div id="cant-enroll-alert">
                                <span className="text-gray-500 ">The Pay Over Time Enrollment deadline has passed this month. Be sure to enroll before the 5th next month.</span>
                            </div>
                        }
                    </div>
                }
                <div className="w-full border-t-2 border-gray-300"></div>
                <ul className="list-disc marker:text-header w-full pl-5">
                    {
                        sortedInvitations.map((invitation, index) => {

                            const { balance, dueDate, autodrawDate, idPayor: invPayor, fullName } = invitation;
                            const displayName = idPayor === invPayor ? `My Payment` : fullName;
                            const displayDueDate = idPayor === invPayor && isAutoPay ? DateTime.fromISO(autodrawDate) : DateTime.fromISO(dueDate);

                            const payments = invitation.payments.map((payment) => {
                                const { createdAt, idTransactionStatus, isAutopayment } = payment;
                                const paymentDate = DateTime.fromISO(createdAt);
                                return (
                                    <div key={payment.idPayment} className="flex flex-row gap-x-2">
                                        <span>
                                            {`Payment on ${paymentDate.toFormat(`DD`)} - ${currency(payment.amount).format()}`}
                                        </span>
                                        <TransactionBadge idTransactionStatus={idTransactionStatus} isAutopay={isAutopayment} />
                                    </div>
                                );
                            });

                            const dueOrAutopay = idPayor === invPayor && isAutoPay ? `Auto-pay date` : `Total due by`;

                            return (
                                <React.Fragment key={invPayor}>
                                    <div className="flex flex-row justify-between">
                                        <li>
                                            <span className="text-2xl font-semibold text-header whitespace-nowrap ">{displayName}</span>
                                        </li>
                                        {
                                            index === 0 && invoiceBalance.intValue !== 0 && isGuarantor &&
                                            <button className="btn btn-outline-primary text-blue-on w-24 h-8 text-sm py-1 px-1 rounded" onClick={modify}>Split this bill</button>
                                        }

                                    </div>
                                    <div className="flex flex-col border-l-2 border-header my-3 pl-3 gap-y-1">
                                        <div className="flex flex-row gap-x-3 text-lg font-semibold">
                                            <span className="text-blue-on font-bold">{currency(balance).format()}</span>
                                        </div>
                                        {payments}
                                        <div className="border-right ">{`${dueOrAutopay} ${displayDueDate.toFormat(`DDD`)}`}</div>
                                    </div>
                                </React.Fragment>
                            );

                        })
                    }
                </ul>
            </div >
        </div >
    );
};

Bill.propTypes = {
    currentBill: PropTypes.object.isRequired,
    alerts: PropTypes.array
};

export default Bill;
